import type {
  StoreProduct,
  StoreProductCategory,
  StoreProductVariant,
} from "@medusajs/types";

export type CatalogExtras = {
  isbn: string | null;
  rating_count: number | null;
  us_restricted: boolean | null;
};

declare module "@medusajs/types" {
  interface StoreProduct {
    catalog_extras?: CatalogExtras | null;
  }
}

export type DisplayPrice = {
  amount: number;
  compareAt: number | null;
  currencyCode: string;
} | null;

export function getDisplayPrice(
  variant: StoreProductVariant | undefined,
): DisplayPrice {
  if (!variant) return null;
  const calc = variant.calculated_price;
  if (!calc) return null;
  const amount = calc.calculated_amount;
  const currencyCode = calc.currency_code;
  if (amount == null || !currencyCode) return null;
  const original = calc.original_amount;
  const compareAt = original != null && original !== amount ? original : null;
  return { amount, compareAt, currencyCode };
}

export type ProductMetadataView = {
  slug: string;
  brandId: string;
  categoryId: string;
  priceAmount: number;
  compareAtAmount: number | null;
  currency: string;
  gradeLabel: string | null;
  gradeMin: number | null;
  gradeMax: number | null;
  format: string | null;
  series: string | null;
  isbn: string | null;
  ratingCount: number;
  publisher: string | null;
};

export type Product = StoreProduct & {
  metadata: ProductMetadataView;
};

const FALLBACK_CURRENCY = "PHP";

function readMetaString(
  meta: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const v = meta?.[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

function readMetaInt(
  meta: Record<string, unknown> | null | undefined,
  key: string,
): number | null {
  const v = meta?.[key];
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === "string" && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  }
  return null;
}

function pickDefaultVariant(
  product: StoreProduct,
): StoreProductVariant | undefined {
  return product.variants?.[0] ?? undefined;
}

function pickPrimaryCategory(
  product: StoreProduct,
): StoreProductCategory | undefined {
  return product.categories?.[0] ?? undefined;
}

export function projectProduct(product: StoreProduct): Product {
  const variant = pickDefaultVariant(product);
  const display = getDisplayPrice(variant);
  const meta = product.metadata ?? null;
  const category = pickPrimaryCategory(product);
  const extras = product.catalog_extras ?? null;
  const metadata: ProductMetadataView = {
    slug: product.handle,
    brandId: product.collection_id ?? "",
    categoryId: category?.id ?? "",
    priceAmount: display?.amount ?? 0,
    compareAtAmount: display?.compareAt ?? null,
    currency: display?.currencyCode ?? FALLBACK_CURRENCY,
    gradeLabel: readMetaString(meta, "grade_label"),
    gradeMin: readMetaInt(meta, "grade_min"),
    gradeMax: readMetaInt(meta, "grade_max"),
    format: readMetaString(meta, "format"),
    series: readMetaString(meta, "series"),
    isbn:
      extras?.isbn ?? readMetaString(meta, "isbn") ?? variant?.barcode ?? null,
    ratingCount: extras?.rating_count ?? readMetaInt(meta, "rating_count") ?? 0,
    publisher:
      readMetaString(meta, "publisher") ?? readMetaString(meta, "brand"),
  };
  return { ...product, metadata };
}

export type ProductImage = {
  id: string;
  productId: string;
  url: string;
  alt: string;
  position: number;
};

export function projectProductImages(product: StoreProduct): ProductImage[] {
  const images = product.images ?? [];
  return images.map((img, idx) => ({
    id: img.id,
    productId: product.id,
    url: img.url,
    alt: product.title,
    position: img.rank ?? idx,
  }));
}

export type Brand = {
  id: string;
  slug: string;
  name: string;
  featured: boolean;
  position: number;
  logoUrl: string | null;
};

export type Category = {
  id: string;
  slug: string;
  name: string;
  imageUrl: string;
  position: number;
};
